import React from "react";
import { Helmet } from "react-helmet";
import { applyHostBranding } from "../../util/brandDomains";

const DEFAULT_TITLE = "Plany365 | Booking, Care, Jobs, and Business Tools";
const DEFAULT_DESCRIPTION =
  "Create booking pages, share business links, find care, post jobs, and manage customer-facing pages with Plany365.";

const toAbsoluteUrl = (value) => {
  if (!value) return undefined;
  if (/^https?:\/\//i.test(value)) return value;
  if (typeof window === "undefined") return value;

  const origin = window.location.origin.replace(/\/$/, "");
  const path = String(value).startsWith("/") ? value : `/${value}`;
  return `${origin}${path}`;
};

const normalizeDescription = (value) => {
  const text = String(value || "")
    .replace(/\s+/g, " ")
    .trim();

  if (!text) return DEFAULT_DESCRIPTION;
  return text.length > 160 ? `${text.slice(0, 157)}...` : text;
};

const SeoMeta = ({
  title,
  description,
  image = "/og-preview.png",
  imageType = "image/png",
  imageWidth = 1200,
  imageHeight = 630,
  url,
  type = "website",
  siteName = "Plany365",
  twitterCard = "summary_large_image",
}) => {
  const normalizedTitle = applyHostBranding(title || DEFAULT_TITLE);
  const normalizedDescription = applyHostBranding(normalizeDescription(description));
  const normalizedImage = toAbsoluteUrl(image || "/og-preview.png");
  const normalizedUrl =
    toAbsoluteUrl(url) ||
    (typeof window !== "undefined" ? window.location.href : "https://plany365.com/");
  const normalizedSiteName = applyHostBranding(siteName);

  return (
    <Helmet>
      <title>{normalizedTitle}</title>
      <meta name="description" content={normalizedDescription} />
      <link rel="canonical" href={normalizedUrl} />

      <meta property="og:type" content={type} />
      <meta property="og:site_name" content={normalizedSiteName} />
      <meta property="og:url" content={normalizedUrl} />
      <meta property="og:title" content={normalizedTitle} />
      <meta property="og:description" content={normalizedDescription} />
      <meta property="og:image" content={normalizedImage} />
      <meta property="og:image:secure_url" content={normalizedImage} />
      <meta property="og:image:type" content={imageType} />
      {imageWidth != null ? <meta property="og:image:width" content={String(imageWidth)} /> : null}
      {imageHeight != null ? <meta property="og:image:height" content={String(imageHeight)} /> : null}

      <meta name="twitter:card" content={twitterCard} />
      <meta name="twitter:title" content={normalizedTitle} />
      <meta name="twitter:description" content={normalizedDescription} />
      <meta name="twitter:image" content={normalizedImage} />
    </Helmet>
  );
};

export default SeoMeta;
